import { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';
import { useEntityAudit } from '../../hooks/useAudit';

interface AuditInfoTooltipProps {
  entityType: string;
  entityId: string;
}

export function AuditInfoTooltip({ entityType, entityId }: AuditInfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: entries, isLoading } = useEntityAudit(entityType, entityId, open);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Find first CREATE entry and last UPDATE/DEACTIVATE entry
  const createEntry = entries?.find((e) => e.action === 'CREATE');
  const updateEntry = entries?.find((e) => e.action === 'UPDATE' || e.action === 'DEACTIVATE');

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className="text-gray-400 hover:text-primary-600 p-1"
        aria-label="Audit info"
        title="Audit info"
      >
        <Info size={16} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-72 text-sm">
          <h4 className="font-semibold text-gray-800 mb-3">Audit Info</h4>
          {isLoading ? (
            <p className="text-gray-500">Loading...</p>
          ) : entries && entries.length > 0 ? (
            <div className="space-y-2">
              {createEntry && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Created by</span>
                    <span className="text-gray-800">{createEntry.userName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Created at</span>
                    <span className="text-gray-800">
                      {new Date(createEntry.createdAt).toLocaleString()}
                    </span>
                  </div>
                </>
              )}
              {updateEntry && (
                <>
                  <div className="flex justify-between border-t border-gray-100 pt-2 mt-2">
                    <span className="text-gray-500">Updated by</span>
                    <span className="text-gray-800">{updateEntry.userName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Updated at</span>
                    <span className="text-gray-800">
                      {new Date(updateEntry.createdAt).toLocaleString()}
                    </span>
                  </div>
                </>
              )}
            </div>
          ) : (
            <p className="text-gray-500">No audit records found</p>
          )}
        </div>
      )}
    </div>
  );
}
